import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import {
  User,
  AuthResponse,
  School,
  Project,
  HistoryEntry,
  Verification,
  DashboardStats,
  ApiResponse,
  MealDispatch,
  DispatchTraceability,
  ProjectTrackerSummary,
  ProjectTrackerDisplayAdjustment,
  ProjectTrackerDisplayAdjustmentRequest,
  VerificationPendingResult,
} from '../types/api';
import websocketService from './websocketService';

export const isRefreshEndpoint = (url: unknown): boolean => {
  if (typeof url !== 'string') return false;
  const path = url.split(/[?#]/, 1)[0].replace(/\/+$/, '');
  return path.endsWith('/api/auth/refresh');
};

export const shouldAttemptTokenRefresh = (
  status: number | undefined,
  requestUrl: unknown,
  alreadyRetried: boolean | undefined
): boolean => status === 401 && !alreadyRetried && !isRefreshEndpoint(requestUrl);

export const getApiErrorMessage = (status: number | undefined, responseBody: any, fallback: string): string => {
  const apiMessage = responseBody?.error?.message || responseBody?.message;
  if (typeof apiMessage === 'string' && apiMessage.trim()) return apiMessage.trim();
  if (status === 429) return 'Too many requests. Please wait a moment and try again.';
  return fallback;
};

class ApiClient {
  private client: AxiosInstance;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private user: User | null = null;
  private pendingGetRequests = new Map<string, Promise<any>>();
  private responseCache = new Map<string, { expiresAt: number; data: any }>();
  private cacheGeneration = 0;
  private readonly persistentCachePrefix = 'nutrishala:http-cache:';

  private static getDefaultApiBaseURL(): string {
    if (typeof window !== 'undefined' && window.location?.hostname) {
      const { hostname, origin, port, protocol } = window.location;
      if (port && port !== '5000') {
        const apiProtocol = protocol === 'https:' ? 'https' : 'http';
        return `${apiProtocol}://${hostname}:5000`;
      }
      if (!port || port === '5000') {
        return origin;
      }
    }
    return 'http://localhost:5000';
  }

  constructor(baseURL: string = process.env.REACT_APP_API_URL || ApiClient.getDefaultApiBaseURL()) {
    this.client = axios.create({
      baseURL,
      withCredentials: true, // Crucial for cookies
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor: Add JWT token
    this.client.interceptors.request.use((config) => {
      if (String(config.method || 'get').toLowerCase() !== 'get') {
        this.clearGetCache();
      }
      if (this.accessToken) {
        config.headers.Authorization = `Bearer ${this.accessToken}`;
      }
      return config;
    });

    // Response interceptor: Handle 401 and refresh token
    this.client.interceptors.response.use(
      (response) => {
        // Global Rename Enforcement for legacy ADM project labels
        if (response.data) {
          try {
            let strData = JSON.stringify(response.data);
            const needsRename =
              strData.toLowerCase().includes('sheet 3') ||
              strData.toLowerCase().includes('sheet3');
            
            if (needsRename) {
              // 1. Specific standard name replacements
              strData = strData.replace(/Sheet 3 Project/gi, 'Project ADM')
                               .replace(/Sheet 3 project/gi, 'Project ADM')
                               .replace(/Sheet3 Project/gi, 'Project ADM')
                               .replace(/Sheet3 project/gi, 'Project ADM');

              // 2. Target names without breaking IDs
              strData = strData.replace(/\bSheet3\b/g, 'Project ADM');
              strData = strData.replace(/\bSheet 3\b/g, 'Project ADM');

              // 3. Clean up double prefixes
              strData = strData.replace(/Project Project/g, 'Project')
                               .replace(/project_Project/g, 'project_'); // Fixes ID corruption
              
              response.data = JSON.parse(strData);
            }

            // Global removal of legacy import metadata
            if (strData.includes('Imported from COFFCO Project School List-2026 workbook.')) {
              strData = strData.replace(/Imported from COFFCO Project School List-2026 workbook\./g, '');
              response.data = JSON.parse(strData);
            }
          } catch (e) {
            // Silently ignore non-JSON or malformed data
          }
        }
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as any;
        error.message = getApiErrorMessage(error.response?.status, error.response?.data, error.message);

        if (shouldAttemptTokenRefresh(error.response?.status, originalRequest?.url, originalRequest?._retry)) {
          originalRequest._retry = true;

          try {
            // refresh endpoint will use the httpOnly cookie automatically
            const response = await this.client.post('/api/auth/refresh');

            const payload = this.unwrapData(response.data);
            const { access_token } = payload;
            this.setTokens(access_token);

            return this.client(originalRequest);
          } catch (refreshError) {
            this.clearAuthenticationState();
            if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    this.loadFromLocalStorage();
    // Mutations may be made by another signed-in browser. Remove mutable API
    // snapshots as soon as the backend announces a change; mounted pages also
    // subscribe and refetch their visible state.
    websocketService.subscribeToSchoolUpdates(() => {
      this.invalidatePersistentCache('/api/schools');
      this.invalidatePersistentCache('/api/projects');
      this.invalidatePersistentCache('/api/dashboard');
    });
    websocketService.subscribeToProjectUpdates(() => {
      this.invalidatePersistentCache('/api/projects');
      this.invalidatePersistentCache('/api/schools');
      this.invalidatePersistentCache('/api/dashboard');
    });
    websocketService.subscribeToVerificationUpdates(() => {
      this.invalidatePersistentCache('/api/verification');
      this.invalidatePersistentCache('/api/projects');
      this.invalidatePersistentCache('/api/dashboard');
    });
  }

  private unwrapData<T>(responseData: ApiResponse<T> | T): T {
    if (responseData && typeof responseData === 'object' && 'data' in responseData) {
      return (responseData as ApiResponse<T>).data as T;
    }
    return responseData as T;
  }

  private stableStringify(value: any): string {
    if (!value || typeof value !== 'object') return String(value ?? '');
    if (Array.isArray(value)) return `[${value.map((item) => this.stableStringify(item)).join(',')}]`;
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${key}:${this.stableStringify(value[key])}`)
      .join(',')}}`;
  }

  private getRequestKey(url: string, config?: AxiosRequestConfig): string {
    return `${url}?${this.stableStringify(config?.params || {})}`;
  }

  private clearGetCache(): void {
    this.pendingGetRequests.clear();
    this.responseCache.clear();
  }

  private readPersistentCache<T>(key: string, ttlMs: number): T | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(`${this.persistentCachePrefix}${key}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || Date.now() - Number(parsed.savedAt || 0) > ttlMs) return null;
      return parsed.data as T;
    } catch {
      return null;
    }
  }

  private writePersistentCache<T>(key: string, data: T): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(`${this.persistentCachePrefix}${key}`, JSON.stringify({ savedAt: Date.now(), data }));
    } catch {
      // Persistent cache is best-effort; memory cache still serves current navigation.
    }
  }

  private invalidatePersistentCache(match: string): void {
    // Mutations must invalidate both cache layers. Previously only localStorage
    // was cleared, so the ten-minute in-memory schools response could continue
    // returning a school without its newly inherited QR template.
    this.cacheGeneration += 1;
    Array.from(this.responseCache.keys())
      .filter((key) => key.includes(match))
      .forEach((key) => this.responseCache.delete(key));
    Array.from(this.pendingGetRequests.keys())
      .filter((key) => key.includes(match))
      .forEach((key) => this.pendingGetRequests.delete(key));

    if (typeof window === 'undefined') return;
    try {
      Object.keys(window.localStorage)
        .filter((key) => key.startsWith(this.persistentCachePrefix) && key.includes(match))
        .forEach((key) => window.localStorage.removeItem(key));
    } catch {
      // Ignore localStorage cleanup failures.
    }
  }

  private async getCached<T>(url: string, config?: AxiosRequestConfig, ttlMs = 15000, force = false, persistMs = 0): Promise<T> {
    const key = this.getRequestKey(url, config);
    const cached = this.responseCache.get(key);
    if (!force && cached && cached.expiresAt > Date.now()) return cached.data as T;

    if (!force && persistMs > 0) {
      const persisted = this.readPersistentCache<T>(key, persistMs);
      if (persisted) {
        this.responseCache.set(key, { data: persisted, expiresAt: Date.now() + ttlMs });
        return persisted;
      }
    }

    const pending = this.pendingGetRequests.get(key);
    if (!force && pending) return pending as Promise<T>;

    const requestGeneration = this.cacheGeneration;
    const request = this.client
      .get<ApiResponse<T>>(url, config)
      .then((response) => {
        const payload = this.unwrapData(response.data);
        // Do not let a GET that started before a mutation repopulate stale data.
        if (requestGeneration === this.cacheGeneration) {
          this.responseCache.set(key, { data: payload, expiresAt: Date.now() + ttlMs });
          if (persistMs > 0) this.writePersistentCache(key, payload);
        }
        return payload;
      })
      .finally(() => {
        this.pendingGetRequests.delete(key);
      });

    this.pendingGetRequests.set(key, request);
    return request;
  }
  private setTokens(accessToken: string): void {
    this.accessToken = accessToken;
    localStorage.setItem('access_token', accessToken);
    if (typeof navigator === 'undefined' || navigator.onLine !== false) {
      websocketService.connect(accessToken).catch((error) => {
        console.warn('WebSocket unavailable after authentication:', error);
      });
    }
  }

  private clearAuthenticationState(): void {
    websocketService.disconnect();
    this.accessToken = null;
    this.refreshToken = null;
    this.user = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    this.clearGetCache();
  }

  private loadFromLocalStorage(): void {
    const accessToken = localStorage.getItem('access_token');
    const userStr = localStorage.getItem('user');

    if (accessToken) {
      this.accessToken = accessToken;
    }

    if (userStr) {
      try {
        this.user = JSON.parse(userStr);
      } catch (e) {
        console.error('Failed to parse user from localStorage', e);
      }
    }
  }

  // Auth endpoints
  async login(username: string, password: string, rememberMe: boolean = false): Promise<AuthResponse> {
    const response = await this.client.post<ApiResponse<AuthResponse>>('/api/auth/login', {
      username,
      password,
      rememberMe,
    });
    const payload = this.unwrapData(response.data);
    const { access_token, user } = payload;
    this.setTokens(access_token);
    this.user = user;
    localStorage.setItem('user', JSON.stringify(user));
    return payload;
  }

  async signup(data: Record<string, any>): Promise<User> {
    const response = await this.client.post<ApiResponse<User>>('/api/auth/register', data);
    return this.unwrapData(response.data);
  }

  async logout(): Promise<void> {
    try {
      await this.client.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearAuthenticationState();
    }
  }

  async getMe(): Promise<User> {
    const payload = await this.getCached<User>('/api/auth/me', undefined, 30000);
    this.user = payload;
    localStorage.setItem('user', JSON.stringify(this.user));
    return payload;
  }

  async requestPasswordReset(email: string): Promise<void> {
    await this.client.post('/api/auth/forgot-password', { email });
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await this.client.post('/api/auth/reset-password', { token, password });
  }

  // Dashboard endpoints
  async getDashboardStats(): Promise<DashboardStats> {
    // CRUD totals must be current when the dashboard opens. Force a network
    // refresh so an older persistent count cannot survive a school mutation
    // performed in another tab or client.
    return this.getCached<DashboardStats>('/api/dashboard', undefined, 15000, true);
  }

  async getPublicStats(): Promise<DashboardStats> {
    return this.getCached<DashboardStats>('/api/dashboard/public-stats', undefined, 60000, true);
  }

  async getQuickStats(): Promise<DashboardStats> {
    return this.getCached<DashboardStats>('/api/dashboard/quick-stats', undefined, 60000, false, 24 * 60 * 60 * 1000);
  }

  async getRecentActivity(): Promise<HistoryEntry[]> {
    const response = await this.client.get<ApiResponse<{ entries: HistoryEntry[]; total: number }>>('/api/history', {
      params: { limit: 10, offset: 0 },
    });
    const payload = this.unwrapData(response.data);
    return Array.isArray(payload) ? payload : payload.entries || [];
  }

  // Schools endpoints
  async getSchools(filters?: Record<string, any>): Promise<School[]> {
    return this.getCached<School[]>('/api/schools', {
      params: { limit: 100, ...(filters || {}) },
    // School CRUD can occur in another tab or admin client. Always refresh the
    // registry from PostgreSQL so deleted rows and served counts cannot remain
    // visible from a 24-hour browser cache.
    }, 15000, true);
  }

  async getSchool(id: string): Promise<School> {
    return this.getCached<School>(`/api/schools/${id}`, undefined, 15000, true);
  }

  async createSchool(data: Omit<School, 'id' | 'created_at' | 'updated_at'>): Promise<School> {
    const response = await this.client.post<ApiResponse<School>>('/api/schools', data);
    this.invalidatePersistentCache('/api/schools');
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/dashboard');
    this.invalidatePersistentCache('/api/verification');
    return this.unwrapData(response.data);
  }

  async updateSchool(id: string, data: Partial<School>): Promise<School> {
    const response = await this.client.put<ApiResponse<School>>(`/api/schools/${id}`, data);
    this.invalidatePersistentCache('/api/schools');
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/dashboard');
    this.invalidatePersistentCache('/api/verification');
    return this.unwrapData(response.data);
  }

  async deleteSchool(id: string): Promise<void> {
    await this.client.delete(`/api/schools/${id}`);
    this.invalidatePersistentCache('/api/schools');
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/dashboard');
    this.invalidatePersistentCache('/api/verification');
  }

  async getSchoolStats(): Promise<Record<string, any>> {
    return this.getCached<Record<string, any>>('/api/schools/stats');
  }

  async getSchoolDistributionHistory(id: string): Promise<Record<string, any>> {
    return this.getCached<Record<string, any>>(`/api/schools/${id}/distribution-history`);
  }

  // Projects endpoints
  async getProjects(filters?: Record<string, any>): Promise<Project[]> {
    const payload = await this.getCached<any>('/api/projects', { params: filters }, 15000, true);
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.projects)) return payload.projects;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  }

  async getProject(id: string): Promise<Project> {
    return this.getCached<Project>(`/api/projects/${id}`, undefined, 15000, true);
  }

  async createProject(data: Record<string, any>): Promise<Project> {
    const response = await this.client.post<ApiResponse<Project>>('/api/projects', data);
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/schools');
    this.invalidatePersistentCache('/api/dashboard');
    return this.unwrapData(response.data);
  }

  async updateProject(id: string, data: Partial<Project>): Promise<Project> {
    const response = await this.client.put<ApiResponse<Project>>(`/api/projects/${id}`, data);
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/schools');
    this.invalidatePersistentCache('/api/dashboard');
    return this.unwrapData(response.data);
  }

  async deleteProject(id: string): Promise<void> {
    await this.client.delete(`/api/projects/${id}`);
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/schools');
    this.invalidatePersistentCache('/api/dashboard');
  }

  async getProjectSchools(id: string): Promise<School[]> {
    return this.getCached<School[]>(`/api/projects/${id}/schools`, undefined, 15000, true);
  }

  async getProjectTrackerSummary(
    id: string,
    type?: string,
    options: { compact?: boolean; force?: boolean } = {}
  ): Promise<ProjectTrackerSummary> {
    return this.getCached<ProjectTrackerSummary>(
      `/api/projects/${id}/trackers`,
      {
        params: {
          ...(type ? { type } : {}),
          ...(options.compact ? { compact: 'true' } : {}),
          ...(options.force ? { refresh: 'true', _ts: Date.now() } : {}),
        },
      },
      15000,
      options.force,
      0
    );
  }

  async exportProjectTrackerCSV(id: string, filters?: Record<string, any>): Promise<Blob> {
    const response = await this.client.get<Blob>(`/api/projects/${id}/trackers/export.csv`, {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  }

  async updateProjectTrackerDisplayAdjustment(
    id: string,
    data: ProjectTrackerDisplayAdjustmentRequest
  ): Promise<ProjectTrackerDisplayAdjustment> {
    const response = await this.client.put<ApiResponse<ProjectTrackerDisplayAdjustment>>(`/api/projects/${id}/trackers/adjustments`, data);
    this.invalidatePersistentCache(`/api/projects/${id}/trackers`);
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/dashboard');
    return this.unwrapData(response.data);
  }

  async attachSchoolToProject(projectId: string, schoolId: string): Promise<School[]> {
    const response = await this.client.post<ApiResponse<School[]>>(`/api/projects/${projectId}/schools`, {
      school_id: schoolId,
    });
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/schools');
    this.invalidatePersistentCache('/api/dashboard');
    return this.unwrapData(response.data);
  }

  // History endpoints
  async getHistory(page: number = 1, limit: number = 50): Promise<any> {
    const response = await this.client.get<ApiResponse<HistoryEntry[]>>('/api/history', {
      params: { offset: (page - 1) * limit, limit },
    });
    const payload = this.unwrapData(response.data);
    return Array.isArray(payload)
      ? { entries: payload, total: (response.data as any).meta?.total || payload.length, limit, page }
      : payload;
  }

  async exportHistory(): Promise<Blob> {
    const response = await this.client.get<Blob>('/api/history/export', {
      responseType: 'blob',
    });
    return response.data;
  }

  // Verification endpoints
  async getPendingVerifications(filters?: Record<string, any>): Promise<VerificationPendingResult> {
    const response = await this.client.get<ApiResponse<Verification[] | VerificationPendingResult>>('/api/verification/pending', {
      params: { limit: 50, ...(filters || {}) },
    });
    const payload = this.unwrapData(response.data);
    if (Array.isArray(payload)) {
      return { entries: payload, total: (response.data as any).meta?.total || payload.length };
    }
    if (Array.isArray((payload as any)?.entries)) {
      return {
        entries: (payload as any).entries,
        total: (payload as any).total ?? (payload as any).entries.length,
        counts_by_type: (payload as any).counts_by_type,
        type: (payload as any).type,
      };
    }
    if (Array.isArray((payload as any)?.data)) {
      return {
        entries: (payload as any).data,
        total: (payload as any).total ?? (payload as any).data.length,
        counts_by_type: (payload as any).counts_by_type,
        type: (payload as any).type,
      };
    }
    return { entries: [], total: 0 };
  }

  async getVerification(id: string): Promise<Verification> {
    return this.getCached<Verification>(`/api/verification/${id}`, undefined, 5000);
  }

  async approveVerification(id: string, notes?: string): Promise<Verification> {
    const response = await this.client.post<ApiResponse<Verification>>(
      `/api/verification/${id}/approve`,
      { notes }
    );
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/dashboard');
    this.invalidatePersistentCache('/api/verification');
    this.invalidatePersistentCache('/api/schools');
    return this.unwrapData(response.data);
  }

  resolveApiUrl(path?: string | null): string {
    if (!path) return '';
    if (/^https?:\/\//i.test(path) || path.startsWith('data:')) return path;
    const baseURL = String(this.client.defaults.baseURL || window.location.origin).replace(/\/+$/, '');
    return `${baseURL}/${path.replace(/^\/+/, '')}`;
  }

  async updateProjectLogo(id: string, image: string): Promise<{ id: string; url: string }> {
    const response = await this.client.post<ApiResponse<{ id: string; url: string }>>(`/api/projects/${id}/logo`, { image });
    this.invalidatePersistentCache('/api/projects');
    return this.unwrapData(response.data);
  }

  async removeProjectLogo(id: string): Promise<void> {
    await this.client.delete(`/api/projects/${id}/logo`);
    this.invalidatePersistentCache('/api/projects');
  }

  async getProjectQrTemplate(id: string): Promise<import('../types/api').ProjectQrTemplateResult> {
    const response = await this.client.get<ApiResponse<import('../types/api').ProjectQrTemplateResult>>('/api/projects/' + id + '/qr-template');
    return this.unwrapData(response.data);
  }

  async updateProjectQrTemplate(id: string, image: string): Promise<import('../types/api').ProjectQrTemplateResult> {
    const response = await this.client.post<ApiResponse<import('../types/api').ProjectQrTemplateResult>>('/api/projects/' + id + '/qr-template', { image });
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/schools');
    return this.unwrapData(response.data);
  }

  async removeProjectQrTemplate(id: string): Promise<void> {
    await this.client.delete('/api/projects/' + id + '/qr-template');
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/schools');
  }

  async getSchoolQrTemplate(id: string): Promise<import('../types/api').QrTemplateAsset | null> {
    const response = await this.client.get<ApiResponse<import('../types/api').QrTemplateAsset | null>>('/api/schools/' + id + '/qr-template');
    return this.unwrapData(response.data);
  }

  async updateSchoolQrTemplate(id: string, image: string): Promise<import('../types/api').QrTemplateAsset> {
    const response = await this.client.post<ApiResponse<import('../types/api').QrTemplateAsset>>('/api/schools/' + id + '/qr-template', { image });
    this.invalidatePersistentCache('/api/schools');
    return this.unwrapData(response.data);
  }

  async removeSchoolQrTemplate(id: string): Promise<import('../types/api').QrTemplateAsset | null> {
    const response = await this.client.delete<ApiResponse<import('../types/api').QrTemplateAsset | null>>('/api/schools/' + id + '/qr-template');
    this.invalidatePersistentCache('/api/schools');
    return this.unwrapData(response.data);
  }

  async getProjectActivities(id: string, includeArchived = false): Promise<import('../types/api').ProjectActivity[]> {
    const response = await this.client.get<ApiResponse<import('../types/api').ProjectActivity[]>>(`/api/projects/${id}/activities`, {
      params: includeArchived ? { include_archived: 'true' } : undefined,
    });
    return this.unwrapData(response.data);
  }

  async createProjectActivity(id: string, data: Record<string, any>): Promise<import('../types/api').ProjectActivity> {
    const response = await this.client.post<ApiResponse<import('../types/api').ProjectActivity>>(`/api/projects/${id}/activities`, data);
    this.invalidatePersistentCache('/api/projects');
    return this.unwrapData(response.data);
  }

  async updateProjectActivity(id: string, activityId: string, data: Record<string, any>): Promise<import('../types/api').ProjectActivity> {
    const response = await this.client.put<ApiResponse<import('../types/api').ProjectActivity>>(`/api/projects/${id}/activities/${activityId}`, data);
    this.invalidatePersistentCache('/api/projects');
    return this.unwrapData(response.data);
  }

  async updateProjectActivityMetrics(id: string, activityId: string, metrics: Record<string, any>[]): Promise<import('../types/api').ProjectActivity> {
    const response = await this.client.put<ApiResponse<import('../types/api').ProjectActivity>>(`/api/projects/${id}/activities/${activityId}/metrics`, { metrics });
    this.invalidatePersistentCache('/api/projects');
    return this.unwrapData(response.data);
  }

  async deleteProjectActivity(id: string, activityId: string): Promise<{ archived: boolean }> {
    const response = await this.client.delete<ApiResponse<{ archived: boolean }>>(`/api/projects/${id}/activities/${activityId}`);
    this.invalidatePersistentCache('/api/projects');
    return this.unwrapData(response.data);
  }

  async reorderProjectActivities(id: string, activityIds: string[]): Promise<import('../types/api').ProjectActivity[]> {
    const response = await this.client.put<ApiResponse<import('../types/api').ProjectActivity[]>>(`/api/projects/${id}/activities/order`, { activity_ids: activityIds });
    this.invalidatePersistentCache('/api/projects');
    return this.unwrapData(response.data);
  }

  async updateProjectActivityPoster(id: string, activityId: string, image: string): Promise<{ id: string; url: string }> {
    const response = await this.client.post<ApiResponse<{ id: string; url: string }>>(`/api/projects/${id}/activities/${activityId}/poster`, { image });
    this.invalidatePersistentCache('/api/projects');
    return this.unwrapData(response.data);
  }

  async removeProjectActivityPoster(id: string, activityId: string): Promise<void> {
    await this.client.delete(`/api/projects/${id}/activities/${activityId}/poster`);
    this.invalidatePersistentCache('/api/projects');
  }

  async getSchoolActivities(schoolId: string): Promise<{ project: Project; activities: import('../types/api').ProjectActivity[] }> {
    const response = await this.client.get<ApiResponse<{ project: Project; activities: import('../types/api').ProjectActivity[] }>>(`/api/track/${schoolId}/activities`);
    return this.unwrapData(response.data);
  }

  async submitProjectActivity(schoolId: string, activityId: string, data: Record<string, any>): Promise<Record<string, any>> {
    const response = await this.client.post<ApiResponse<Record<string, any>>>(`/api/track/${schoolId}/activities/${activityId}`, data);
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/verification');
    return this.unwrapData(response.data);
  }

  async rejectVerification(id: string, rejection_reason: string, notes?: string): Promise<Verification> {
    const response = await this.client.post<ApiResponse<Verification>>(
      `/api/verification/${id}/reject`,
      { reason: rejection_reason, rejection_reason, notes }
    );
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/dashboard');
    this.invalidatePersistentCache('/api/verification');
    this.invalidatePersistentCache('/api/schools');
    return this.unwrapData(response.data);
  }

  async getMealForm(schoolId: string): Promise<School> {
    return this.getCached<School>(`/api/verification/meal-form/${schoolId}`, undefined, 30000);
  }

  async submitMealDistribution(schoolId: string, data: Record<string, any>): Promise<Verification> {
    const response = await this.client.post<ApiResponse<Verification>>(`/api/verification/meal-form/${schoolId}`, data);
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/dashboard');
    this.invalidatePersistentCache('/api/verification');
    this.invalidatePersistentCache('/api/schools');
    return this.unwrapData(response.data);
  }

  async submitTrackerEvidence(schoolId: string, trackerType: string, data: Record<string, any>): Promise<Verification> {
    const response = await this.client.post<ApiResponse<Verification>>(
      `/api/verification/tracker/${schoolId}/${trackerType}`,
      data
    );
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/dashboard');
    this.invalidatePersistentCache('/api/verification');
    this.invalidatePersistentCache('/api/schools');
    return this.unwrapData(response.data);
  }

  async updateVerificationSubmission(id: string, notes: Record<string, any>): Promise<Verification> {
    const response = await this.client.put<ApiResponse<Verification>>(`/api/verification/${id}/submission`, { notes });
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/dashboard');
    this.invalidatePersistentCache('/api/verification');
    this.invalidatePersistentCache('/api/schools');
    return this.unwrapData(response.data);
  }

  async deleteVerification(id: string): Promise<void> {
    await this.client.delete(`/api/verification/${id}`);
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/dashboard');
    this.invalidatePersistentCache('/api/verification');
    this.invalidatePersistentCache('/api/schools');
  }

  async getTrackContext(schoolId: string): Promise<School> {
    return this.getCached<School>(`/api/track/${schoolId}/context`, undefined, 30000);
  }

  async getLatestDispatch(schoolId: string): Promise<MealDispatch | null> {
    return this.getCached<MealDispatch | null>(`/api/track/${schoolId}/latest-dispatch`, undefined, 30000);
  }

  async getDispatchTraceability(dispatchId: string): Promise<DispatchTraceability> {
    const response = await this.client.get<ApiResponse<DispatchTraceability>>(
      `/api/track/dispatch/${dispatchId}/traceability`
    );
    return this.unwrapData(response.data);
  }

  async getDispatches(filters?: Record<string, any>): Promise<{ dispatches: MealDispatch[]; total: number }> {
    const response = await this.client.get<ApiResponse<MealDispatch[] | { dispatches: MealDispatch[]; total: number }>>(
      '/api/dispatches',
      { params: { limit: 50, ...(filters || {}) } }
    );
    const payload = this.unwrapData(response.data);
    if (Array.isArray(payload)) {
      return { dispatches: payload, total: (response.data as any).meta?.total || payload.length };
    }
    return {
      dispatches: Array.isArray((payload as any)?.dispatches) ? (payload as any).dispatches : [],
      total: (payload as any)?.total || 0,
    };
  }

  async createDispatch(data: Record<string, any>): Promise<MealDispatch> {
    const response = await this.client.post<ApiResponse<MealDispatch>>('/api/dispatches', data);
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/schools');
    this.invalidatePersistentCache('/api/dashboard');
    this.invalidatePersistentCache('/api/track');
    return this.unwrapData(response.data);
  }

  async updateDispatch(id: string, data: Record<string, any>): Promise<MealDispatch> {
    const response = await this.client.put<ApiResponse<MealDispatch>>(`/api/dispatches/${id}`, data);
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/schools');
    this.invalidatePersistentCache('/api/dashboard');
    this.invalidatePersistentCache('/api/track');
    return this.unwrapData(response.data);
  }

  async deleteDispatch(id: string): Promise<void> {
    await this.client.delete(`/api/dispatches/${id}`);
    this.invalidatePersistentCache('/api/projects');
    this.invalidatePersistentCache('/api/schools');
    this.invalidatePersistentCache('/api/dashboard');
    this.invalidatePersistentCache('/api/track');
  }

  async exportDispatches(filters?: Record<string, any>): Promise<Blob> {
    const response = await this.client.get<Blob>('/api/dispatches/export.csv', {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  }

  // Utility methods
  getUser(): User | null {
    return this.user;
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  isStatisticsViewer(): boolean {
    const username = this.user?.username?.toLowerCase();
    const email = this.user?.email?.toLowerCase();
    return username === 'stats' || email === 'stats@millets.local';
  }

  isCommandUser(): boolean {
    return this.isAuthenticated() && !this.isStatisticsViewer();
  }

  isAdmin(): boolean {
    return this.user?.role === 'admin';
  }

  isVerifier(): boolean {
    return this.user?.role === 'verifier';
  }

  canWrite(): boolean {
    return this.isAdmin();
  }

  isReadOnlyUser(): boolean {
    return this.isAuthenticated() && !this.isStatisticsViewer() && !this.canWrite();
  }

  canExportOperationalData(): boolean {
    return this.isAdmin();
  }
}

const apiClient = new ApiClient();
export default apiClient;
